import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import i18n from '@/i18n'
import { ROUTES } from '@/constants'
import { createChatSocket } from '@/services/chatSocket'
import { getConversation } from '@/services/conversationService'
import {
  requestNotificationPermissionOnFirstInteraction,
  showBrowserNotification,
} from '@/services/browserNotifications'
import type {
  ChatSocketInstance,
  CallOfferEvent,
  CallEndedEvent,
  CallCancelledEvent,
  CallTakenEvent,
} from '@/services/chatSocket'
import type {
  ConversationListResponse,
  ConversationMessageEvent,
  ConversationNewEvent,
  ConversationStatusChangedEvent,
} from '@/types/Conversation'
import {
  appendLiveThreadEntry,
  invalidateThread,
} from '@/pages/Conversations/hooks/useConversationThread'
import { ringingCallsReducer } from './ringingCalls'
import {
  CALL_WAITING_TONE,
  INCOMING_CALL_TONE,
  MESSAGE_TONE,
  TRANSFER_TONE,
  playTone,
} from './notificationTones'
import { useTabAttention } from './useTabAttention'

const CONVERSATIONS_QUERY_KEY = ['conversations'] as const

// Why a conversation is asking for the agent — picks the tone and the
// wording of the browser notification.
type AttentionNotice =
  | { kind: 'message'; contactName: string; content: string }
  | { kind: 'transfer'; contactName: string }

export interface ConversationsLocationState {
  contactPhone?: string
}

function describeNotice(notice: AttentionNotice): { title: string; body: string } {
  if (notice.kind === 'transfer') {
    return {
      title: i18n.t('attention.transfer_title'),
      body: i18n.t('attention.transfer_body', { name: notice.contactName }),
    }
  }
  return {
    title: i18n.t('attention.message_title', { name: notice.contactName }),
    body: notice.content || i18n.t('attention.message_media_body'),
  }
}

export interface CallActions {
  answerCall: (payload: { callId: string; sdp: string }) => void
  notifyCallMediaConnected: (payload: { callId: string }) => void
  hangupCall: (payload: { callId: string }) => void
  rejectCall: (payload: { callId: string }) => void
  onCallEnded: (handler: (event: CallEndedEvent) => void) => void
  offCallEnded: (handler: (event: CallEndedEvent) => void) => void
  onCallCancelled: (handler: (event: CallCancelledEvent) => void) => void
  offCallCancelled: (handler: (event: CallCancelledEvent) => void) => void
  onCallTaken: (handler: (event: CallTakenEvent) => void) => void
  offCallTaken: (handler: (event: CallTakenEvent) => void) => void
}

interface SocketContextValue {
  unreadIds: Set<string>
  markRead: (id: string) => void
  clearUnread: () => void
  setSelectedConversationId: (id: string | null) => void
  ringingCalls: CallOfferEvent[]
  clearIncomingCall: (callId: string) => void
  setAgentOnCall: (onCall: boolean) => void
  callActions: CallActions
}

const NOOP_CALL_ACTIONS: CallActions = {
  answerCall: () => undefined,
  notifyCallMediaConnected: () => undefined,
  hangupCall: () => undefined,
  rejectCall: () => undefined,
  onCallEnded: () => undefined,
  offCallEnded: () => undefined,
  onCallCancelled: () => undefined,
  offCallCancelled: () => undefined,
  onCallTaken: () => undefined,
  offCallTaken: () => undefined,
}

const SocketContext = createContext<SocketContextValue>({
  unreadIds: new Set(),
  markRead: () => undefined,
  clearUnread: () => undefined,
  setSelectedConversationId: () => undefined,
  ringingCalls: [],
  clearIncomingCall: () => undefined,
  setAgentOnCall: () => undefined,
  callActions: NOOP_CALL_ACTIONS,
})

export function SocketProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  // useNavigate's identity isn't guaranteed stable, and the socket effect
  // must not be torn down (reconnecting the socket) just because it changed.
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate
  // Keyed by contactPhone, not messageDocId — stable across a contact's
  // session transitions (closing and reopening a conversation).
  const [unreadIds, setUnreadIds] = useState<Set<string>>(new Set())
  // Every call currently ringing for this agent, oldest first. A single slot
  // would let each new offer overwrite the previous one, losing it for the
  // whole team since offers are broadcast to every agent.
  const [ringingCalls, dispatchRingingCalls] = useReducer(ringingCallsReducer, [])
  const ringingCallsRef = useRef(ringingCalls)
  ringingCallsRef.current = ringingCalls
  const agentOnCallRef = useRef(false)
  const selectedContactPhoneRef = useRef<string | null>(null)
  const socketRef = useRef<ChatSocketInstance | null>(null)
  // Calls this agent already answered, rejected, or saw end — a late or
  // re-delivered call:offer (e.g. after a socket.io reconnect) for one of
  // these must not bring the banner back.
  const handledCallIdsRef = useRef<Set<string>>(new Set())
  // Kept here rather than on the socket itself: children's effects run
  // before this provider's, so useCall would otherwise subscribe while
  // socketRef.current is still null, and lose its handlers whenever the
  // socket is recreated.
  const callEndedHandlersRef = useRef<Set<(event: CallEndedEvent) => void>>(new Set())
  const callCancelledHandlersRef = useRef<Set<(event: CallCancelledEvent) => void>>(new Set())
  const callTakenHandlersRef = useRef<Set<(event: CallTakenEvent) => void>>(new Set())

  // Stable identities: read socketRef.current at call time instead of
  // depending on it, so callers (e.g. useCall) don't need to resubscribe
  // just because the underlying socket connection was recreated.
  const callActions = useRef<CallActions>({
    answerCall: (payload) => socketRef.current?.answerCall(payload),
    notifyCallMediaConnected: (payload) =>
      socketRef.current?.notifyCallMediaConnected(payload),
    hangupCall: (payload) => socketRef.current?.hangupCall(payload),
    rejectCall: (payload) => socketRef.current?.rejectCall(payload),
    onCallEnded: (handler) => {
      callEndedHandlersRef.current.add(handler)
    },
    offCallEnded: (handler) => {
      callEndedHandlersRef.current.delete(handler)
    },
    onCallCancelled: (handler) => {
      callCancelledHandlersRef.current.add(handler)
    },
    offCallCancelled: (handler) => {
      callCancelledHandlersRef.current.delete(handler)
    },
    onCallTaken: (handler) => {
      callTakenHandlersRef.current.add(handler)
    },
    offCallTaken: (handler) => {
      callTakenHandlersRef.current.delete(handler)
    },
  }).current

  // The backend doesn't reliably echo call:taken/call:ended back to the
  // agent who acted on the call, so the banner is cleared locally as soon
  // as this agent answers or rejects.
  const clearIncomingCall = useCallback((callId: string) => {
    handledCallIdsRef.current.add(callId)
    dispatchRingingCalls({ type: 'remove', callId })
  }, [])

  const setAgentOnCall = useCallback((onCall: boolean) => {
    agentOnCallRef.current = onCall
  }, [])

  const addUnread = useCallback((contactPhone: string, notice: AttentionNotice) => {
    const pageVisible = document.visibilityState === 'visible'
    // An open thread only counts as "seen" while the agent can actually see
    // the tab — otherwise it still has to flag the tab and notify.
    if (pageVisible && contactPhone === selectedContactPhoneRef.current) return

    setUnreadIds((prev) => new Set([...prev, contactPhone]))
    playTone(notice.kind === 'transfer' ? TRANSFER_TONE : MESSAGE_TONE)

    if (!pageVisible) {
      showBrowserNotification({
        ...describeNotice(notice),
        tag: contactPhone,
        onClick: () => {
          const state: ConversationsLocationState = { contactPhone }
          navigateRef.current(ROUTES.CONVERSATIONS, { state })
        },
      })
    }
  }, [])

  const markRead = useCallback((contactPhone: string) => {
    setUnreadIds((prev) => {
      const next = new Set(prev)
      next.delete(contactPhone)
      return next
    })
  }, [])

  const clearUnread = useCallback(() => setUnreadIds(new Set()), [])

  const setSelectedConversationId = useCallback((contactPhone: string | null) => {
    selectedContactPhoneRef.current = contactPhone
  }, [])

  useTabAttention(unreadIds.size)

  useEffect(() => requestNotificationPermissionOnFirstInteraction(), [])

  // Anything that arrived for the open thread while the tab was in the
  // background was flagged unread (see addUnread) — coming back to the tab
  // is what actually shows it to the agent.
  useEffect(() => {
    const handleVisibilityChange = () => {
      const selected = selectedContactPhoneRef.current
      if (document.visibilityState === 'visible' && selected) {
        markRead(selected)
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [markRead])

  useEffect(() => {
    const socket = createChatSocket()
    socketRef.current = socket

    // The server replays every still-ringing call right after (re)connecting,
    // so the queue is rebuilt from that authoritative replay — dropping any
    // entry whose cancel was missed while the socket was down.
    const handleConnect = () => {
      dispatchRingingCalls({ type: 'clear' })
    }

    const handleCallOffer = (event: CallOfferEvent) => {
      if (handledCallIdsRef.current.has(event.callId)) return

      const alreadyRinging = ringingCallsRef.current.some(
        (call) => call.callId === event.callId,
      )
      dispatchRingingCalls({ type: 'offer', call: event })
      if (alreadyRinging) return

      playTone(agentOnCallRef.current ? CALL_WAITING_TONE : INCOMING_CALL_TONE)
    }

    const handleCallTaken = (event: CallTakenEvent) => {
      clearIncomingCall(event.callId)
      callTakenHandlersRef.current.forEach((handler) => handler(event))
    }

    const handleCallCancelled = (event: CallCancelledEvent) => {
      clearIncomingCall(event.callId)
      callCancelledHandlersRef.current.forEach((handler) => handler(event))
    }

    const handleCallEnded = (event: CallEndedEvent) => {
      clearIncomingCall(event.callId)
      callEndedHandlersRef.current.forEach((handler) => handler(event))
    }

    socket.onConnect(handleConnect)
    socket.onCallOffer(handleCallOffer)
    socket.onCallTaken(handleCallTaken)
    socket.onCallCancelled(handleCallCancelled)
    socket.onCallEnded(handleCallEnded)

    const handleNew = (event: ConversationNewEvent) => {
      void queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEY })
      // A brand new session for this contact — if their thread happens to
      // be open right now, its cached pages are for the old session set.
      invalidateThread(queryClient, event.contactPhone)
    }

    const handleMessage = (event: ConversationMessageEvent) => {
      let shouldNotify = false
      let contactPhone: string | null = null
      let contactName: string | null = null

      queryClient.setQueriesData<ConversationListResponse>(
        { queryKey: CONVERSATIONS_QUERY_KEY },
        (current) => {
          if (!current) return current
          return {
            ...current,
            data: current.data.map((c) => {
              if (c._id !== event.messageDocId) return c

              contactPhone = c.contactPhone
              contactName = c.contactName ?? c.contactPhone

              if (
                event.entry.direction === 'inbound' &&
                c.sessionStatus === 'agent_handling'
              ) {
                shouldNotify = true
              }

              return {
                ...c,
                lastEntry: event.entry,
                updatedAt: new Date().toISOString(),
              }
            }),
          }
        },
      )

      // The socket payload only carries messageDocId, not contactPhone —
      // looked up from the list cache above instead of touching the
      // backend event shape just for this. Misses only in the narrow race
      // right after a brand new session starts, before the list refetch
      // from handleNew lands; self-heals on the very next message.
      if (contactPhone) {
        appendLiveThreadEntry(queryClient, contactPhone, {
          ...event.entry,
          messageDocId: event.messageDocId,
        })

        if (shouldNotify) {
          addUnread(contactPhone, {
            kind: 'message',
            contactName: contactName ?? contactPhone,
            content: event.entry.content,
          })
        }
      }
    }

    const handleStatusChanged = (event: ConversationStatusChangedEvent) => {
      let contactPhone: string | null = null
      let contactName: string | null = null

      queryClient.setQueriesData<ConversationListResponse>(
        { queryKey: CONVERSATIONS_QUERY_KEY },
        (current) => {
          if (!current) return current
          return {
            ...current,
            data: current.data.map((c) => {
              if (c._id !== event.messageDocId) return c

              contactPhone = c.contactPhone
              contactName = c.contactName ?? c.contactPhone

              return {
                ...c,
                status: event.status,
                sessionStatus: event.sessionStatus ?? c.sessionStatus,
                ...(event.lastContext !== undefined
                  ? { lastContext: event.lastContext }
                  : {}),
              }
            }),
          }
        },
      )

      // The patch above only updates rows already on screen — a status
      // change also moves the conversation between the status filters, so
      // every cached list (not just the visible one) is refetched.
      void queryClient.invalidateQueries({
        queryKey: CONVERSATIONS_QUERY_KEY,
        refetchType: 'all',
      })

      if (event.sessionStatus !== 'waiting_agent') return

      if (contactPhone) {
        addUnread(contactPhone, {
          kind: 'transfer',
          contactName: contactName ?? contactPhone,
        })
        return
      }

      // Not in any cached list (past the first page, filtered out, or the
      // agent hasn't opened Conversations yet) — the event only carries
      // messageDocId, so the contact is looked up before flagging it.
      getConversation(event.messageDocId)
        .then((conversation) => {
          addUnread(conversation.contactPhone, {
            kind: 'transfer',
            contactName: conversation.contactName ?? conversation.contactPhone,
          })
        })
        .catch(() => undefined)
    }

    socket.onConversationNew(handleNew)
    socket.onConversationMessage(handleMessage)
    socket.onConversationStatusChanged(handleStatusChanged)

    return () => {
      socket.offConversationNew(handleNew)
      socket.offConversationMessage(handleMessage)
      socket.offConversationStatusChanged(handleStatusChanged)
      socket.offConnect(handleConnect)
      socket.offCallOffer(handleCallOffer)
      socket.offCallTaken(handleCallTaken)
      socket.offCallCancelled(handleCallCancelled)
      socket.offCallEnded(handleCallEnded)
      socket.disconnect()
      socketRef.current = null
    }
  }, [queryClient, addUnread, clearIncomingCall])

  return (
    <SocketContext.Provider
      value={{
        unreadIds,
        markRead,
        clearUnread,
        setSelectedConversationId,
        ringingCalls,
        clearIncomingCall,
        setAgentOnCall,
        callActions,
      }}
    >
      {children}
    </SocketContext.Provider>
  )
}

export function useSocketContext(): SocketContextValue {
  return useContext(SocketContext)
}

export type { CallOfferEvent, CallEndedEvent, CallCancelledEvent, CallTakenEvent }
